import {useMemo} from 'react';
import type {BreadcrumbItem} from 'src/app/layout/BackBar';
import {PathUtility} from 'src/shared/constants/PathBuilder';
import type {TreeAncestor} from 'src/shared/utils/treeUtils';

export type DocumentManagementBreadcrumbMode =
  | 'list'
  | 'folder-list'
  | 'view-document'
  | 'create-folder'
  | 'edit-folder'
  | 'create-document'
  | 'edit-document';

export interface UseDocumentManagementBreadcrumbsProps {
  mode: DocumentManagementBreadcrumbMode;
  nodeId?: string | number;
  parentId?: string | number;
  nodeName?: string;
  /** Ancestor path from root to the current node (exclusive of current). Used to build full breadcrumb trails. */
  ancestors?: TreeAncestor[];
}

export interface UseDocumentManagementBreadcrumbsResult {
  breadcrumbs: BreadcrumbItem[];
  fallbackTo: string;
}

/**
 * Builds ancestor breadcrumb items from an ancestors array.
 * Each ancestor becomes a clickable link to its folder.
 */
const buildAncestorCrumbs = (ancestors: TreeAncestor[]): BreadcrumbItem[] => {
  return ancestors.map(ancestor => ({
    label: ancestor.name,
    to: PathUtility.DocumentsManagerList(ancestor.id),
  }));
};

export const useDocumentManagementBreadcrumbs = ({
  mode,
  nodeId,
  parentId,
  nodeName,
  ancestors,
}: UseDocumentManagementBreadcrumbsProps): UseDocumentManagementBreadcrumbsResult => {
  return useMemo(() => {
    const crumbs: BreadcrumbItem[] = [];
    let fallbackTo = PathUtility.DocumentsManagerList();

    // Always start with root "Documents" link
    const rootCrumb: BreadcrumbItem = {
      label: 'Documents',
      to: PathUtility.DocumentsManagerList(),
    };

    // Build ancestor crumbs if available (excludes the current node)
    const ancestorCrumbs =
      ancestors && ancestors.length > 0 ? buildAncestorCrumbs(ancestors) : [];

    switch (mode) {
      case 'list': {
        crumbs.push({label: 'Documents'});
        break;
      }

      case 'folder-list': {
        fallbackTo = PathUtility.DocumentsManagerList(parentId);
        crumbs.push(rootCrumb);
        crumbs.push(...ancestorCrumbs);
        crumbs.push({label: nodeName || 'Folder'});
        break;
      }

      case 'view-document': {
        fallbackTo = PathUtility.DocumentsManagerList(parentId);
        crumbs.push(rootCrumb);
        crumbs.push(...ancestorCrumbs);
        crumbs.push({label: nodeName || 'Document'});
        break;
      }

      case 'create-folder': {
        fallbackTo = PathUtility.DocumentsManagerList(parentId);
        crumbs.push(rootCrumb);
        crumbs.push(...ancestorCrumbs);
        crumbs.push({label: 'New Folder'});
        break;
      }

      case 'edit-folder': {
        fallbackTo = PathUtility.DocumentsManagerList(nodeId);
        crumbs.push(rootCrumb);
        crumbs.push(...ancestorCrumbs);
        crumbs.push({
          label: nodeName || 'Folder',
          to: PathUtility.DocumentsManagerList(nodeId),
        });
        crumbs.push({label: 'Edit'});
        break;
      }

      case 'create-document': {
        fallbackTo = PathUtility.DocumentsManagerList(parentId);
        crumbs.push(rootCrumb);
        crumbs.push(...ancestorCrumbs);
        crumbs.push({label: 'New Document'});
        break;
      }

      case 'edit-document': {
        fallbackTo = PathUtility.DocumentManagerViewFile(nodeId!, 'content');
        crumbs.push(rootCrumb);
        crumbs.push(...ancestorCrumbs);
        crumbs.push({
          label: nodeName || 'Document',
          to: PathUtility.DocumentManagerViewFile(nodeId!, 'content'),
        });
        crumbs.push({label: 'Edit'});
        break;
      }
    }

    return {breadcrumbs: crumbs, fallbackTo};
  }, [mode, nodeId, parentId, nodeName, ancestors]);
};
